import { navigatorDebugObject } from "./components/NavigationTree2/navigatorDebug";
import { elementIsVisibleInViewport, getTreeviewHtmlElement } from "./components/NavigationTree2";
import { isDevelopmentEnvironment } from "./estada-config";
import BaseModel from "./models/BaseModel";
import BaseObject, { Event, newId } from "./models/BaseObject";
import { sleep } from "./models/utils";
import { DisplaySelections } from "./DisplaySelections";
import { VirtualConnectionSelection } from "./connectionAnalyser/VirtualConnectionSelection";
import type { ConnectionAnalyser } from "./connectionAnalyser/ConnectionAnalyser";
import type { VirtualConnection } from "./connectionAnalyser/VirtualConnections";
import type Channel from "./models/Channel";
import type Connection from "./models/Connection";

export default class SelectionManager extends BaseObject {
  public get className(): string {
    return "SelectionManager"; // no itx
  }

  get isPersistent(): boolean {
    return false;
  }

  onBeforeSelectionChanges = new Event();
  onAfterSelectionChanged = new Event();

  _selectedObject: BaseModel | undefined;
  readonly displaySelections = new DisplaySelections();
  /** Explicit editor pins survive ordinary selection changes, including deselection. */
  readonly editorPins = new DisplaySelections();

  /** Navigator paths and independent editor retention are coordinated by DockManager. */
  private updateDisplaySelections(object: BaseModel | undefined, preserveButtonEditor = false): void {
    state.dockManager.updateDisplaySelections(object, preserveButtonEditor);
  }

  _highLigthedObjects = new Array<BaseModel>();
  private pendingScrollTimers = new Set<ReturnType<typeof setTimeout>>();

  addHeightLightedObject(obj: BaseModel) {
    obj.highlighted = true;
    this._highLigthedObjects.push(obj);

    obj.raiseOnChange("highlighted");
    this.raiseOnChange("highlighted");

    const timer = setTimeout(() => {
      this.pendingScrollTimers.delete(timer);
      this.scrollSelectedObjectIntoView(obj);
    }, 500);
    this.pendingScrollTimers.add(timer);
  }

  focusedObjectInNavigator: BaseModel | undefined;

  clearAdditionalHighlights() {
    for (const timer of this.pendingScrollTimers) {
      clearTimeout(timer);
    }
    this.pendingScrollTimers.clear();
    this._highLigthedObjects.forEach((obj) => {
      delete obj.highlighted;
      obj.raiseOnChange("highlighted");
    });

    this._highLigthedObjects = [];
  }

  get selectedObject(): BaseModel | undefined {
    return this._selectedObject;
  }

  set selectedObject(newValue: BaseModel | undefined) {
    if (newValue && !newValue.isBaseModel) {
      return;
    }

    state.connectionAnalyser.selectObject(newValue);

    this.updateDisplaySelections(newValue);

    if (this._selectedObject != newValue) {
      this.clearAdditionalHighlights();

      this.setSelectedObjectWithoutScrolling(newValue);
      this.scrollSelectedObjectIntoView();
      this.raiseOnChange("selectedObject");
    }
  }

  async scrollSelectedObjectIntoView(obj?: BaseModel) {
    obj ||= this._selectedObject;
    if (obj) {
      // The mobile shell owns its navigator. Elsewhere retain the wait for a tree still mounting.
      const mobileNavigatorActive = () => document.querySelector("#C1_App .C1_mobileApp") !== null;
      if (mobileNavigatorActive()) return;
      console.log("[NavigatorDebug] scroll: start", { object: navigatorDebugObject(obj) });

      for (let i = 0; i < 10; i++) {
        if (mobileNavigatorActive()) return;
        const elm = getTreeviewHtmlElement("navigator", obj); // no itx
        if (!elm) {
          await sleep(100);
        } else {
          const s = elementIsVisibleInViewport(elm);
          console.log("[NavigatorDebug] scroll: row found", { object: navigatorDebugObject(obj), attempt: i + 1, viewportOffset: s });
          if (s != 0) {
            elm?.scrollIntoView({
              behavior: "smooth",
              block: "center", // no itx
            });
          }
          return;
        }
      }
      console.log("[NavigatorDebug] scroll: row missing after 10 attempts", { object: navigatorDebugObject(obj) });
    }
  }

  async focusSelectedObjectInTreeview() {
    if (this.selectedObject) {
      await this.scrollSelectedObjectIntoView();

      const elm = getTreeviewHtmlElement("navigator", this._selectedObject); // no itx

      // const elm = document.querySelector(
      //   `#navigationTree #${treeViewNodeId(this.selectedObject.id)}`,
      // ) as HTMLDivElement;

      // const elm = document.getElementById(
      //   treeViewNodeId(this.selectedObject.id),
      // ) as HTMLAnchorElement;
      elm?.focus();

      this._selectedObject?.raiseOnChange("focusInTreeview");
    }
  }

  setSelectedObjectWithoutNavigatorUpdate(newValue: BaseModel | undefined) {
    this.updateDisplaySelections(newValue);
    state.connectionAnalyser.selectObject(newValue);
    if (newValue != this._selectedObject) {
      if (this._selectedObject) {
        this._selectedObject.selected = false;
        this._selectedObject.raiseOnChange("selected");
      }

      this._selectedObject = newValue;

      //      if (isDevelopmentEnvironment()) {
      (window as any).selectedObject = this._selectedObject;
      //      }

      // raiseOnChange for new object

      if (this._selectedObject) {
        this._selectedObject.selected = true;
        this._selectedObject.raiseOnChange("selected");
        this.raiseOnChange("selectedObj");
      }

      state.contextMenuManager.currentObject = newValue;
    }
  }

  /** Shows a key's consumers in the navigator and selects its first or explicitly chosen connection. */
  selectButtonEditorChannel(channel: Channel, selectedConnection?: Connection): void {
    const connections = channel.connections.asArray().filter(connection => {
      const consumer = connection.getPartner(channel);
      return !connection.isDeleted && consumer?.isConsumer && !consumer.isDeleted
        && (!selectedConnection || connection === selectedConnection);
    });
    const consumers = connections.map(connection => connection.getPartner(channel)!);
    this.clearAdditionalHighlights();
    this.updateDisplaySelections(channel);
    state.connectionAnalyser.selectObject(channel);
    this.setSelectedObjectWithoutScrolling(connections[0] ?? channel, true);
    this.displaySelections.setNavigatorObjects([channel, ...consumers]);
    if (consumers[0]) void this.scrollSelectedObjectIntoView(consumers[0]);
  }

  /** Selects derived wiring through the same app selection path as a real connection. */
  selectVirtualConnection(connection: VirtualConnection, analyser: ConnectionAnalyser): void {
    this.clearAdditionalHighlights();
    this.setSelectedObjectWithoutNavigatorUpdate(new VirtualConnectionSelection(analyser, connection.id));
    analyser.selectedVirtualConnectionId.value = connection.id;
  }

  /** Clears a deleted connection's selection while retaining the analysis and open button editor. */
  clearSelectionPreservingAnalysis(): void {
    this.clearAdditionalHighlights();
    this.setSelectedObjectWithoutScrolling(undefined, true);
  }

  setSelectedObjectWithoutScrolling(newValue: BaseModel | undefined, preserveAnalysis = false) {
    this.updateDisplaySelections(newValue, preserveAnalysis);
    if (!preserveAnalysis) state.connectionAnalyser.selectObject(newValue);
    //
    if (newValue != this._selectedObject) {
      // raiseOnChange for deselected object

      this.onBeforeSelectionChanges.raise(this._selectedObject, newValue);

      if (this._selectedObject) {
        this._selectedObject.selected = false;
        this._selectedObject.raiseOnChange("selected");
      }

      this._selectedObject = newValue;
      if (isDevelopmentEnvironment()) {
        (window as any).selectedObject = this._selectedObject;
      }

      // raiseOnChange for new object
      if (this._selectedObject) {
        this._selectedObject.selected = true;
        this._selectedObject.raiseOnChange("selected");
        // etsNavigate(this._selectedObject);  // For future use! Not to be deleted.
      }
      state.contextMenuManager.currentObject = newValue;

      this.onAfterSelectionChanged.raise(this._selectedObject, newValue);
      // raiseOnChange for SelectionManager

      this.raiseOnChange();
    }
  }

  clear() {
    this.displaySelections.clear();
    state.connectionAnalyser.selectObject(undefined);
    this.clearAdditionalHighlights();

    if (this._selectedObject) {
      this._selectedObject.selected = false;
      this._selectedObject.raiseOnChange();
    }
    this._selectedObject = undefined;
    this.focusedObjectInNavigator = undefined;
    if (isDevelopmentEnvironment()) {
      (window as any).selectedObject = undefined;
    }
    this.raiseOnChange();
  }
}
